import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Filter, X, AlertTriangle, CheckCircle, Eye, Clock, ChevronLeft, ChevronRight, Loader2, Bell, ShieldAlert, TrendingUp, Clock3, CheckCircle2 } from 'lucide-react'
import { Card, Badge } from '../atoms'
import { cn } from '../atoms'
import { SearchBar } from '../molecules/SearchBar'
import { useAlerts, useAlertStats, useAcknowledgeAlert, useResolveAlert } from '../../hooks/useAlerts'
import { LiveIndicator } from '../molecules/LiveIndicator'
import { useDebounce } from '../../hooks/useDebounce'
import { useSuccessToast, useErrorToast } from '../atoms/Toaster'
import { useAuth } from '../../store/auth.store'
import { IndicatorCard } from '../molecules/IndicatorCard'

const severityStyles: Record<string, string> = {
  critical: 'bg-red-50 border-red-200',
  high: 'bg-yellow-50 border-yellow-200',
  medium: 'bg-blue-50 border-blue-200',
  low: 'bg-gray-50 border-gray-200',
}

const statusColors: Record<string, string> = {
  OPEN: 'bg-red-100 text-red-700 border border-red-200',
  ACKNOWLEDGED: 'bg-amber-100 text-amber-700 border border-amber-200',
  IN_PROGRESS: 'bg-blue-100 text-blue-700 border border-blue-200',
  RESOLVED: 'bg-green-100 text-green-700 border border-green-200',
  FALSE_POSITIVE: 'bg-gray-100 text-gray-600 border border-gray-200',
}

const STATUS_OPTIONS = [
  { value: '', label: 'Todos los estados' },
  { value: 'OPEN', label: 'Abierta' },
  { value: 'ACKNOWLEDGED', label: 'Reconocida' },
  { value: 'IN_PROGRESS', label: 'En progreso' },
  { value: 'RESOLVED', label: 'Resuelta' },
  { value: 'FALSE_POSITIVE', label: 'Falso positivo' },
]

const SEVERITY_OPTIONS = [
  { value: '', label: 'Todas las severidades' },
  { value: 'critical', label: 'Crítica' },
  { value: 'high', label: 'Alta' },
  { value: 'medium', label: 'Media' },
  { value: 'low', label: 'Baja' },
]

const TYPE_OPTIONS = [
  { value: '', label: 'Todos los tipos' },
  { value: 'legal_risk', label: 'Riesgo Legal' },
  { value: 'sla_breach', label: 'Incumplimiento SLA' },
  { value: 'operational_alert', label: 'Alerta Operativa' },
]

export function AlertsPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState(() => searchParams.get('status') || '')
  const [severityFilter, setSeverityFilter] = useState(() => searchParams.get('severity') || '')
  const [typeFilter, setTypeFilter] = useState(() => searchParams.get('type') || '')
  const [showFilters, setShowFilters] = useState(() => !!(searchParams.get('status') || searchParams.get('severity') || searchParams.get('type')))
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(20)
  const debouncedSearch = useDebounce(search, 300)

  useEffect(() => {
    const next = new URLSearchParams(searchParams)
    if (statusFilter) next.set('status', statusFilter); else next.delete('status')
    if (severityFilter) next.set('severity', severityFilter); else next.delete('severity')
    if (typeFilter) next.set('type', typeFilter); else next.delete('type')
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true })
    }
  }, [statusFilter, severityFilter, typeFilter])

  const { data, isLoading, dataUpdatedAt, isFetching } = useAlerts({
    page,
    limit,
    status: statusFilter || undefined,
    severity: severityFilter || undefined,
    type: typeFilter || undefined,
    search: debouncedSearch || undefined,
  })
  const { data: stats } = useAlertStats()
  const ackMutation = useAcknowledgeAlert()
  const resolveMutation = useResolveAlert()
  const successToast = useSuccessToast()
  const errorToast = useErrorToast()

  const alerts = data?.data || []
  const pagination = data?.pagination
  const totalPages = pagination?.totalPages || 1
  const currentPage = pagination?.page || 1
  const hasActiveFilters = statusFilter || severityFilter || typeFilter

  const clearFilters = () => {
    setStatusFilter('')
    setSeverityFilter('')
    setTypeFilter('')
    setPage(1)
  }

  const toggleStatusFilter = (st: string) => {
    setStatusFilter(prev => prev === st ? '' : st)
    setSeverityFilter('')
    setTypeFilter('')
    setPage(1)
  }

  const toggleSeverityFilter = (sev: string) => {
    setSeverityFilter(prev => prev === sev ? '' : sev)
    setStatusFilter('')
    setTypeFilter('')
    setPage(1)
  }

  const toggleTypeFilter = (tp: string) => {
    setTypeFilter(prev => prev === tp ? '' : tp)
    setStatusFilter('')
    setSeverityFilter('')
    setPage(1)
  }

  const handleAcknowledge = async (id: string) => {
    try {
      await ackMutation.mutateAsync(id)
      successToast('Alerta reconocida')
    } catch {
      errorToast('Error al reconocer alerta')
    }
  }

  const handleResolve = async (id: string) => {
    try {
      await resolveMutation.mutateAsync({ id })
      successToast('Alerta resuelta')
    } catch {
      errorToast('Error al resolver alerta')
    }
  }

  const goToPage = (p: number) => {
    if (p >= 1 && p <= totalPages) setPage(p)
  }

  const getPageNumbers = (): number[] => {
    const pages: number[] = []
    const maxVisible = 5
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2))
    let end = Math.min(totalPages, start + maxVisible - 1)
    if (end - start < maxVisible - 1) {
      start = Math.max(1, end - maxVisible + 1)
    }
    for (let i = start; i <= end; i++) pages.push(i)
    return pages
  }

  const statData = {
    total: stats?.total || 0,
    open: stats?.byStatus?.['OPEN'] || 0,
    acknowledged: stats?.byStatus?.['ACKNOWLEDGED'] || 0,
    resolved: stats?.byStatus?.['RESOLVED'] || 0,
    critical: stats?.bySeverity?.['critical'] || 0,
    sla: stats?.byType?.['sla_breach'] || 0,
  }

  return (
    <div className="space-y-5 max-w-[1700px] mx-auto">
      <header className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-950 dark:text-gray-100">Alertas</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-gray-400">Monitoreo y notificaciones del sistema</p>
        </div>
        <LiveIndicator lastUpdatedAt={dataUpdatedAt} isRefreshing={isFetching} />
      </header>

      <section className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3" aria-label="Resumen de alertas">
        <IndicatorCard
          label="Total alertas"
          value={statData.total}
          icon={Bell}
          tone="slate"
          active={!statusFilter && !severityFilter && !typeFilter}
          onClick={clearFilters}
        />
        <IndicatorCard
          label="Abiertas"
          value={statData.open}
          icon={AlertTriangle}
          tone="red"
          active={statusFilter === 'OPEN'}
          onClick={() => toggleStatusFilter('OPEN')}
        />
        <IndicatorCard
          label="Reconocidas"
          value={statData.acknowledged}
          icon={Eye}
          tone="amber"
          active={statusFilter === 'ACKNOWLEDGED'}
          onClick={() => toggleStatusFilter('ACKNOWLEDGED')}
        />
        <IndicatorCard
          label="Críticas"
          value={statData.critical}
          icon={ShieldAlert}
          tone="red"
          active={severityFilter === 'critical'}
          onClick={() => toggleSeverityFilter('critical')}
        />
        <IndicatorCard
          label="SLA en riesgo"
          value={statData.sla}
          icon={Clock3}
          tone="purple"
          active={typeFilter === 'sla_breach'}
          onClick={() => toggleTypeFilter('sla_breach')}
        />
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:bg-gray-900 dark:border-gray-700">
        <div className="flex flex-col xl:flex-row gap-3">
          <div className="flex-1 min-w-[220px]">
            <SearchBar
              value={search}
              onChange={(v) => { setSearch(v); setPage(1) }}
              mode="simple"
              placeholder="Buscar alertas..."
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={cn(
                'inline-flex items-center gap-2 px-3 py-2 text-sm font-medium border rounded-lg transition-colors',
                showFilters || hasActiveFilters
                  ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 border-primary-300 dark:border-primary-700'
                  : 'text-slate-700 dark:text-gray-200 bg-white dark:bg-gray-800 border-slate-300 dark:border-gray-600 hover:bg-slate-50 dark:hover:bg-gray-700'
              )}
            >
              <Filter className="w-4 h-4" />
              Más filtros
            </button>
            <select
              value={limit}
              onChange={(e) => { setLimit(Number(e.target.value)); setPage(1) }}
              className="text-sm border border-slate-300 rounded-lg px-3 py-2 bg-white text-slate-900"
              title="Alertas por página"
            >
              {[10, 20, 50, 100].map(l => <option key={l} value={l}>{l} / pág</option>)}
            </select>
          </div>
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3 mt-4 pt-4 border-t border-slate-100 dark:border-gray-700">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Estado</label>
              <select
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setPage(1) }}
                className="block w-full px-3 py-2 text-sm border border-slate-300 rounded-lg bg-white text-slate-900 focus:ring-2 focus:ring-primary-500"
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Severidad</label>
              <select
                value={severityFilter}
                onChange={(e) => { setSeverityFilter(e.target.value); setPage(1) }}
                className="block w-full px-3 py-2 text-sm border border-slate-300 rounded-lg bg-white text-slate-900 focus:ring-2 focus:ring-primary-500"
              >
                {SEVERITY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Tipo</label>
              <select
                value={typeFilter}
                onChange={(e) => { setTypeFilter(e.target.value); setPage(1) }}
                className="block w-full px-3 py-2 text-sm border border-slate-300 rounded-lg bg-white text-slate-900 focus:ring-2 focus:ring-primary-500"
              >
                {TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="inline-flex items-center gap-1 px-3 py-2 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                >
                  <X className="w-4 h-4" />
                  Limpiar filtros
                </button>
              )}
            </div>
          </div>
        )}
      </section>

      <Card className="overflow-hidden rounded-xl border-slate-200 shadow-sm">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
          </div>
        ) : alerts.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-12">No hay alertas</p>
        ) : (
          <>
            <div className="overflow-hidden">
              <table className="data-table w-full table-fixed">
                <thead>
                  <tr>
                    <th className="w-[32%]">Alerta</th>
                    <th className="w-[14%]">Severidad</th>
                    <th className="w-[14%]">Estado</th>
                    <th className="w-[18%]">Caso</th>
                    <th className="w-[12%]">Fecha</th>
                    <th className="w-[10%]">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {alerts.map((alert: any) => (
                    <tr key={alert.id} onClick={() => navigate(`/alerts/${alert.id}`)} className="cursor-pointer">
                      <td>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{alert.title}</p>
                          <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 line-clamp-1">{alert.description}</p>
                        </div>
                      </td>
                      <td>
                        <span className={cn(
                          'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                          (alert.severity?.name || alert.severity) === 'critical' ? 'bg-red-100 text-red-700 border border-red-300' :
                          (alert.severity?.name || alert.severity) === 'high' ? 'bg-amber-100 text-amber-700 border border-amber-300' :
                          (alert.severity?.name || alert.severity) === 'medium' ? 'bg-blue-100 text-blue-700 border border-blue-300' :
                          'bg-gray-100 text-gray-600 border border-gray-300'
                        )}>
                          {alert.severity?.label || alert.severity?.name || alert.severity}
                        </span>
                      </td>
                      <td>
                        <span className={cn('inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium', statusColors[alert.status] || 'bg-gray-100 text-gray-600')}>
                          {alert.status?.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td>
                        {alert.case ? (
                          <span className="text-xs text-primary-600 dark:text-primary-400 font-medium truncate block">
                            #{alert.case.caseNumber || alert.case.subject}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                      <td>
                        <span className="flex items-center gap-1 text-sm whitespace-nowrap text-gray-500">
                          <Clock className="w-3 h-3" />
                          {new Date(alert.createdAt).toLocaleDateString('es-CO', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-1">
                          {alert.status !== 'RESOLVED' && alert.status !== 'ACKNOWLEDGED' && (
                            <>
                              <button
                                onClick={() => handleAcknowledge(alert.id)}
                                className="p-1.5 text-gray-400 hover:text-amber-600 dark:hover:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg transition-colors"
                                title="Reconocer"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleResolve(alert.id)}
                                className="p-1.5 text-gray-400 hover:text-green-600 dark:hover:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors"
                                title="Resolver"
                              >
                                <CheckCircle className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 rounded-b-lg">
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Mostrando <span className="font-medium">{((currentPage - 1) * limit) + 1}</span> –{' '}
                  <span className="font-medium">{Math.min(currentPage * limit, pagination?.total || 0)}</span> de{' '}
                  <span className="font-medium">{pagination?.total || 0}</span> alertas
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage <= 1}
                    className="inline-flex items-center p-1.5 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    title="Anterior"
                    aria-label="Página anterior"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>

                  {getPageNumbers().map((p) => (
                    <button
                      key={p}
                      onClick={() => goToPage(p)}
                      className={cn(
                        'inline-flex items-center justify-center min-w-[2rem] h-8 px-2 rounded-md text-sm font-medium transition-colors',
                        p === currentPage
                          ? 'bg-primary-600 text-white border border-primary-600'
                          : 'border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
                      )}
                      aria-label={`Ir a página ${p}`}
                      aria-current={p === currentPage ? 'page' : undefined}
                    >
                      {p}
                    </button>
                  ))}

                  <button
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage >= totalPages}
                    className="inline-flex items-center p-1.5 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    title="Siguiente"
                    aria-label="Página siguiente"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  )
}
